/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ImageUrlGenerator } from "@tivocorp/data-domain-lib";
import type { ShowflixMoreLikeThisResponse } from "@tivocorp/data-domain-lib";

import { CarouselTemplate } from "../../model/enums";
import type {
  EpisodeInfo,
  SeasonsData,
  UpcomingOfferV2
} from "../../model/types";
import { getTilePixelDimensions } from "../utility";
import {
  EXTRA_SEASON_THRESHOLD,
  formatSeasonEpisode,
  getSeasonLabel,
  getVideoResolutionLabel
} from "./contentDetailStrings";
import type {
  ContentDetailEpisodeData,
  ContentDetailRecommendedData,
  ContentDetailSeasonData,
  ContentDetailUpcomingData
} from "./contentDetail.types";

/**
 * Maps the episode-guide and upcoming-offers responses onto the explore
 * sections' state. Pure — every clock- or config-dependent value is passed
 * in, so these are unit-testable with plain fixtures.
 *
 * Card composition mirrors Android's `EpisodeGuideSectionLoader
 * .toEpisodeGuideCarouselItem` and `UpcomingSectionLoader
 * .toUpcomingCarouselItem`; the extra `overlayLabel` is the "S5:E14" chip
 * Figma draws in the gradient scrim at the card's bottom-left, which the
 * Android card component does not have.
 */

const imageUrlGenerator: ImageUrlGenerator = new ImageUrlGenerator();

/** Both explore rails use the landscape 16:9 card (Figma: 424 × 238). */
const RAIL_TEMPLATE: CarouselTemplate = CarouselTemplate.LANDSCAPE_HORIZONTAL;

/** Percent → 0–1 fraction for the tile's progress sliver. */
const PERCENT_SCALE: number = 100;

/** `EpisodeInfo.percentWatched` at or above this counts as fully watched. */
const FULLY_WATCHED_PERCENT: number = 100;

/**
 * Builds a rail thumbnail URL for a content id.
 *
 * @param imageHostBaseUrl - Image host from TSRI; `""` disables images
 * @param objectId - `contentId` of the episode / airing
 * @param imageType - Image-service type, e.g. "EPISODE" or "MOVIE"
 * @returns The composed URL, or `undefined` when no host is configured
 */
function buildRailImageUrl(
  imageHostBaseUrl: string,
  objectId: string,
  imageType: string
): string | undefined {
  if (imageHostBaseUrl === "" || objectId === "") {
    return undefined;
  }
  const { width, height } = getTilePixelDimensions(RAIL_TEMPLATE);
  return imageUrlGenerator.buildImageUrl(
    imageHostBaseUrl,
    objectId,
    imageType as Parameters<ImageUrlGenerator["buildImageUrl"]>[2],
    width,
    height
  );
}

/**
 * Formats an ISO-8601 date as the short numeric date used in card metadata
 * and the focused-item block ("03/21/2025"). Returns `undefined` for a
 * missing or unparseable value.
 */
export function formatShortDate(
  isoDate: string | undefined
): string | undefined {
  if (isoDate === undefined || isoDate === "") {
    return undefined;
  }
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  const pad = (value: number): string => String(value).padStart(2, "0");
  return (
    pad(parsed.getMonth() + 1) +
    "/" +
    pad(parsed.getDate()) +
    "/" +
    String(parsed.getFullYear())
  );
}

/**
 * Formats the "S5:E14" overlay chip drawn over a card thumbnail. Returns
 * `undefined` unless both halves are known.
 */
export function formatOverlayLabel(
  seasonOrYear: number | undefined,
  episodeNumber: number | undefined
): string | undefined {
  if (seasonOrYear === undefined || episodeNumber === undefined) {
    return undefined;
  }
  return "S" + String(seasonOrYear) + ":E" + String(episodeNumber);
}

/**
 * Derives the season picker entries from a `SeasonsData` response.
 *
 * Copies Android's `toSeasonValues` exactly: drop extras (`seasonOrYear < 0`),
 * de-dupe, sort descending. The descending order is what puts the newest
 * season first in the picker.
 *
 * @param seasonsData - The seasons response
 * @returns Picker entries, newest first
 */
export function mapSeasons(
  seasonsData: SeasonsData
): ContentDetailSeasonData[] {
  const seasons = seasonsData.seasons;
  if (seasons === undefined) {
    return [];
  }
  const seen = new Set<number>();
  const values: number[] = [];
  for (const season of seasons) {
    const value: number = season.seasonOrYear;
    if (value >= EXTRA_SEASON_THRESHOLD && !seen.has(value)) {
      seen.add(value);
      values.push(value);
    }
  }
  values.sort((left: number, right: number): number => right - left);
  return values.map(
    (seasonOrYear: number): ContentDetailSeasonData => ({
      id: String(seasonOrYear),
      label: getSeasonLabel(seasonOrYear, seasonsData.grouping),
      seasonOrYear
    })
  );
}

/**
 * Maps one `EpisodeInfo` onto an Episodes rail tile.
 *
 * @param episode - The episode-guide entry
 * @param imageHostBaseUrl - Image host from TSRI; `""` disables thumbnails
 * @returns The rail tile data
 */
export function mapEpisode(
  episode: EpisodeInfo,
  imageHostBaseUrl: string
): ContentDetailEpisodeData {
  const seasonEpisode: string | undefined = formatSeasonEpisode(
    episode.seasonOrYear,
    episode.episodeNum
  );
  const firstAired: string | undefined = formatShortDate(
    episode.firstAiredDate
  );

  return {
    id: episode.contentId,
    title:
      episode.title !== undefined && episode.title.length > 0
        ? episode.title
        : episode.contentId,
    // Android drops the date entirely once it has a season/episode pair
    // (`buildEpisodeGuideMetadata` returns `seasonEpisodeText ?: firstAired`).
    subtitle: seasonEpisode !== undefined ? seasonEpisode : firstAired,
    overlayLabel: formatOverlayLabel(episode.seasonOrYear, episode.episodeNum),
    thumbnailUrl: buildRailImageUrl(
      imageHostBaseUrl,
      episode.contentId,
      "EPISODE"
    ),
    progress: mapWatchedProgress(episode),
    seasonOrYear: episode.seasonOrYear,
    episodeNumber: episode.episodeNum,
    isNew: episode.isNew,
    firstAiredDate: episode.firstAiredDate,
    recordingId: episode.recordingId,
    recordingStatus: episode.recordingStatus
  };
}

/**
 * Converts `percentWatched` / `fullyWatched` into the 0–1 progress sliver the
 * tile renders. Returns `undefined` for an unwatched episode so no sliver is
 * drawn at all.
 */
function mapWatchedProgress(episode: EpisodeInfo): number | undefined {
  if (episode.fullyWatched === true) {
    return 1;
  }
  const percent: number | undefined = episode.percentWatched;
  if (percent === undefined || percent <= 0) {
    return undefined;
  }
  return Math.min(percent, FULLY_WATCHED_PERCENT) / PERCENT_SCALE;
}

/** Image-service type for an upcoming airing, from its `contentType`. */
function upcomingImageType(contentType: string | undefined): string {
  if (contentType === "episode") {
    return "EPISODE";
  }
  if (contentType === "movie") {
    return "MOVIE";
  }
  return "COLLECTION";
}

/**
 * Maps one `UpcomingOfferV2` onto an Upcoming rail tile.
 *
 * @param offer - The upcoming-offers entry
 * @param imageHostBaseUrl - Image host from TSRI; `""` disables thumbnails
 * @param formatTime - Clock formatter for the start-time subtitle
 * @returns The rail tile data
 */
export function mapUpcomingOffer(
  offer: UpcomingOfferV2,
  imageHostBaseUrl: string,
  formatTime: (value: Date) => string
): ContentDetailUpcomingData {
  const startTime = new Date(offer.startTime);
  const subtitle: string | undefined = Number.isNaN(startTime.getTime())
    ? undefined
    : formatTime(startTime);

  const displayTitle: string =
    offer.episodeTitle !== undefined && offer.episodeTitle.length > 0
      ? offer.episodeTitle
      : offer.title;

  return {
    id: offer.offerId,
    contentId: offer.contentId,
    title: displayTitle,
    subtitle,
    schedule: buildUpcomingMetadata(offer),
    overlayLabel: formatOverlayLabel(offer.seasonNumber, offer.episodeNumber),
    imageUrl: buildRailImageUrl(
      imageHostBaseUrl,
      offer.contentId,
      upcomingImageType(offer.contentType)
    ),
    seasonOrYear: offer.seasonNumber,
    episodeNumber: offer.episodeNumber,
    isNew: offer.isNew,
    isLive: offer.isLive,
    originalAirDate: offer.originalAirDate
  };
}

const METADATA_SEPARATOR: string = " • ";

/**
 * Builds the card metadata line, e.g. "S1 E4 • HD".
 *
 * Mirrors Android's `buildProgramContent`: the season/episode pair wins over
 * the original air date (the date is only a fallback), and the resolution is
 * always appended when known.
 */
function buildUpcomingMetadata(offer: UpcomingOfferV2): string | undefined {
  const parts: string[] = [];
  const seasonEpisode: string | undefined = formatSeasonEpisode(
    offer.seasonNumber,
    offer.episodeNumber
  );
  if (seasonEpisode !== undefined) {
    parts.push(seasonEpisode);
  } else {
    const airDate: string | undefined = formatShortDate(offer.originalAirDate);
    if (airDate !== undefined) {
      parts.push(airDate);
    }
  }
  const resolution: string | undefined = getVideoResolutionLabel(
    offer.videoResolution
  );
  if (resolution !== undefined && resolution.length > 0) {
    parts.push(resolution);
  }
  return parts.length > 0 ? parts.join(METADATA_SEPARATOR) : undefined;
}

/**
 * Derives the Upcoming section's season picker entries from the airings
 * already loaded.
 *
 * Figma frame `2198-6084` shows a season picker over the Upcoming rail;
 * neither the upcoming-offers service nor Android provides one, so the
 * chips are grouped client-side from `seasonNumber`. Sorted ascending —
 * upcoming airings run forward in time, so the earliest season comes first
 * (unlike the Episodes picker, which is newest-first).
 *
 * @param items - The airings loaded so far
 * @returns Picker entries, or `[]` when no airing carries a season number
 */
export function deriveUpcomingSeasons(
  items: ContentDetailUpcomingData[]
): ContentDetailSeasonData[] {
  const seen = new Set<number>();
  const values: number[] = [];
  for (const item of items) {
    const value: number | undefined = item.seasonOrYear;
    if (
      value !== undefined &&
      value >= EXTRA_SEASON_THRESHOLD &&
      !seen.has(value)
    ) {
      seen.add(value);
      values.push(value);
    }
  }
  values.sort((left: number, right: number): number => left - right);
  return values.map(
    (seasonOrYear: number): ContentDetailSeasonData => ({
      id: String(seasonOrYear),
      label: getSeasonLabel(seasonOrYear),
      seasonOrYear
    })
  );
}

/** The May Also Like rail uses the portrait 2:3 poster card (Figma: 250 × 376). */
const RECOMMENDED_TEMPLATE: CarouselTemplate =
  CarouselTemplate.POSTER_HORIZONTAL;

type MoreLikeThisItem = ShowflixMoreLikeThisResponse["items"][number];

/**
 * Builds a May Also Like tile thumbnail URL for a feed item.
 *
 * @param imageHostBaseUrl - Image host from TSRI; `""` disables images
 * @param item - The more-like-this entry
 * @returns The composed URL, or `undefined` when no host is configured
 */
function buildRecommendedImageUrl(
  imageHostBaseUrl: string,
  item: MoreLikeThisItem
): string | undefined {
  if (imageHostBaseUrl === "") {
    return undefined;
  }
  const { width, height } = getTilePixelDimensions(RECOMMENDED_TEMPLATE);
  const imageType = imageUrlGenerator.getImageTypeFromFeedItemType(
    item.feedItemType
  );
  return imageUrlGenerator.buildImageUrl(
    imageHostBaseUrl,
    item.feedItemId,
    imageType,
    width,
    height
  );
}

/**
 * Maps one `MoreLikeThisItem` onto a May Also Like rail tile.
 *
 * @param item - The more-like-this entry
 * @param imageHostBaseUrl - Image host from TSRI; `""` disables thumbnails
 * @returns The rail tile data
 */
export function mapMoreLikeThisItem(
  item: MoreLikeThisItem,
  imageHostBaseUrl: string
): ContentDetailRecommendedData {
  return {
    id: item.feedItemId,
    itemType: item.feedItemType,
    title: item.title,
    imageUrl: buildRecommendedImageUrl(imageHostBaseUrl, item)
  };
}
